import "./DiscussionsPanel.css";

import { ChevronDownDarkIcon } from "../../assets/icons";
import { GenericModal } from "../../route/developer/components";
import { ErrorBox } from "../cards/ErrorBox/ErrorBox";
import { Button } from "../forms/Button";
import { Column, Row } from "../layout/Grid/Grid";
import { DiscussionsPanelSkeleton } from "./components/DiscussionsPanelSkeleton";
import { DiscussionThreadItem } from "./components/DiscussionThreadItem";
import { DiscussionTogglePill } from "./components/DiscussionTogglePill";
import { DiscussionsPanelProps, MAX_DISCUSSION_LENGTH, useDiscussionsPanel } from "./useDiscussionsPanel";

export const DiscussionsPanel = (props: DiscussionsPanelProps): JSX.Element => {
  const { variant = "seed", font, headerSize = "default", errors, isLoading = false } = props;

  const {
    showResolved,
    openThreadUuid,
    hasMoreThreads,
    hasMoreResolved,
    newCommentText,
    replyDrafts,
    editState,
    pendingDelete,
    activeThreads,
    resolvedThreads,
    currentUserUuid,
    canCreate,
    canResolve,
    toasts,
    toggleShowResolved,
    handleOpenThread,
    handleShowMore,
    handleShowMoreResolved,
    handleNewCommentChange,
    handleAddComment,
    handleReplyDraftChange,
    handleSaveReply,
    handleCancelReply,
    handleStartEditThread,
    handleStartEditReply,
    handleEditDraftChange,
    handleSaveEdit,
    handleCancelEdit,
    handleDeleteThread,
    handleDeleteReply,
    handleConfirmDelete,
    handleCancelDelete,
    handleResolveThread,
    dismissToast,
  } = useDiscussionsPanel(props);

  const isReplyDelete = pendingDelete?.type === "reply";

  if (isLoading) {
    return <DiscussionsPanelSkeleton />;
  }

  const newCommentOverLimit = newCommentText.length > MAX_DISCUSSION_LENGTH;
  const newCommentNearLimit = !newCommentOverLimit && MAX_DISCUSSION_LENGTH - newCommentText.length <= 50;
  let newCommentCounterSuffix = "";
  if (newCommentOverLimit) newCommentCounterSuffix = " DiscussionTextInput_counterError";
  else if (newCommentNearLimit) newCommentCounterSuffix = " DiscussionTextInput_counterNear";

  return (
    <div className={`DiscussionsPanel DiscussionsPanel_${variant} DiscussionsPanel_${font}`}>
      {/* Header */}
      <div className="DiscussionsPanel__header">
        {headerSize === "small" ? <h3>Discussions</h3> : <h2>Discussions</h2>}
        <button className="DiscussionsPanel__toggleRow" onClick={toggleShowResolved} aria-pressed={showResolved}>
          <span className="DiscussionsPanel__toggleLabel">Show resolved discussions</span>
          <DiscussionTogglePill on={showResolved} />
        </button>
      </div>
      {errors && errors.length > 0 && (
        <Row spacingV="ml">
          <Column span={12}>
            <ErrorBox text={errors.map((error) => error.message)} />
          </Column>
        </Row>
      )}

      {/* Toasts */}
      {toasts.length > 0 && (
        <div className="DiscussionsPanel_toasts" role="status" aria-live="polite">
          {toasts.map((toast) => (
            <div key={toast.id} className={`DiscussionToast DiscussionToast_${toast.type}`}>
              <span className="DiscussionToast_text">{toast.text}</span>
              <button
                type="button"
                className="DiscussionToast_dismiss"
                onClick={() => dismissToast(toast.id)}
                aria-label="Dismiss notification"
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      {/* New comment input — only if user can create */}
      {canCreate && (
        <div className="DiscussionsPanel__newComment">
          <textarea
            className={`DiscussionsPanel__newCommentArea${newCommentOverLimit ? " DiscussionsPanel_newCommentAreaError" : ""}`}
            placeholder="Start a discussion"
            value={newCommentText}
            onChange={(e) => handleNewCommentChange(e.target.value)}
          />
          {newCommentText.length > 0 && (
            <div className="DiscussionsPanel_newCommentMeta">
              <span className={`DiscussionTextInput_counter${newCommentCounterSuffix}`}>
                {newCommentText.length}/{MAX_DISCUSSION_LENGTH}
              </span>
            </div>
          )}
          <div className="DiscussionsPanel__newCommentButtons">
            <Button
              variant="primary"
              size="small"
              text="Add comment"
              onClick={handleAddComment}
              disabled={newCommentOverLimit}
            />
          </div>
        </div>
      )}

      {/* Active thread list */}
      {activeThreads.length > 0 && (
        <div className="DiscussionsPanel__threadList">
          {activeThreads.map((thread) => (
            <DiscussionThreadItem
              key={thread.uuid}
              thread={thread}
              isOpen={openThreadUuid === thread.uuid}
              replyDraft={replyDrafts[thread.uuid] ?? ""}
              editState={editState}
              currentUserUuid={currentUserUuid}
              canCreate={canCreate}
              canResolve={canResolve}
              onToggleOpen={handleOpenThread}
              onReplyDraftChange={handleReplyDraftChange}
              onSaveReply={handleSaveReply}
              onCancelReply={handleCancelReply}
              onStartEditThread={handleStartEditThread}
              onStartEditReply={handleStartEditReply}
              onEditDraftChange={handleEditDraftChange}
              onSaveEdit={handleSaveEdit}
              onCancelEdit={handleCancelEdit}
              onDeleteThread={handleDeleteThread}
              onDeleteReply={handleDeleteReply}
              onResolveThread={handleResolveThread}
            />
          ))}
        </div>
      )}

      {/* Show more — active threads */}
      {hasMoreThreads && (
        <button className="DiscussionsPanel__showMore" onClick={handleShowMore}>
          <ChevronDownDarkIcon />
          Show more
        </button>
      )}

      {/* Resolved section — messages and replies are read-only */}
      {showResolved && resolvedThreads.length > 0 && (
        <div className="DiscussionsPanel__resolvedSection">
          <div className="DiscussionsPanel__resolvedHeader">
            <span className="DiscussionsPanel__resolvedTitle">Resolved discussions</span>
            <hr className="DiscussionsPanel__resolvedRule" />
          </div>
          {resolvedThreads.map((thread) => (
            <DiscussionThreadItem
              key={thread.uuid}
              thread={thread}
              isOpen={openThreadUuid === thread.uuid}
              isResolved
              replyDraft={replyDrafts[thread.uuid] ?? ""}
              editState={editState}
              currentUserUuid={currentUserUuid}
              canCreate={false}
              onToggleOpen={handleOpenThread}
              onReplyDraftChange={handleReplyDraftChange}
              onSaveReply={handleSaveReply}
              onCancelReply={handleCancelReply}
              onStartEditThread={handleStartEditThread}
              onStartEditReply={handleStartEditReply}
              onEditDraftChange={handleEditDraftChange}
              onSaveEdit={handleSaveEdit}
              onCancelEdit={handleCancelEdit}
              onDeleteThread={handleDeleteThread}
              onDeleteReply={handleDeleteReply}
              onResolveThread={handleResolveThread}
            />
          ))}
          {hasMoreResolved && (
            <button className="DiscussionsPanel__showMore" onClick={handleShowMoreResolved}>
              <ChevronDownDarkIcon />
              Show more
            </button>
          )}
        </div>
      )}

      <GenericModal
        show={pendingDelete !== null}
        onConfirm={handleConfirmDelete}
        closeModal={handleCancelDelete}
        data={{
          title: isReplyDelete ? "Delete message?" : "Delete discussion?",
          text: [
            isReplyDelete
              ? "Are you sure you want to delete this message? This action cannot be undone."
              : "Are you sure you want to delete this discussion? This action cannot be undone.",
          ],
          confirmText: "Yes",
          cancelText: "No",
        }}
      />
    </div>
  );
};
